// src/components/RouteProgress.tsx
//
// The thin bar at the very top of the window that tracks "something is
// loading". Two things feed it:
//
// 1. Lazy route chunks — <RouteFallback /> is the Suspense fallback, and
// it holds the bar open for as long as it is mounted.
// 2. React Query fetches — any in-flight query counts.
//
// Behaviour is the familiar one: creep towards 90% while work is
// outstanding (never reaching it, so the bar never lies about being nearly
// done), then snap to 100% and fade out. A hard floor on visible duration
// stops the bar strobing on fast cached navigations.

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useIsFetching } from "@tanstack/react-query";

// --- a tiny external store for manual holds -----------------------------
// Kept outside React so a Suspense fallback can register a hold during
// render-blocking work, which a context provider could not observe.

let holds = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function startRouteLoad(): () => void {
  holds += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
    emit();
  };
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

const getHolds = () => holds;

/** How long the bar stays visible at minimum, so fast loads still read as
 *  a deliberate transition rather than a flicker. */
const MIN_VISIBLE_MS = 320;
/** Delay before showing at all — navigations that resolve instantly should
 *  produce no bar whatsoever. */
const SHOW_DELAY_MS = 80;

export function RouteProgress() {
  const manualHolds = useSyncExternalStore(subscribe, getHolds, getHolds);
  const fetching = useIsFetching();
  const busy = manualHolds > 0 || fetching > 0;

  const [visible, setVisible] = useState(false);
  const [pct, setPct] = useState(0);
  const shownAt = useRef(0);

  useEffect(() => {
    let showTimer: ReturnType<typeof setTimeout>;
    let hideTimer: ReturnType<typeof setTimeout>;
    let creep: ReturnType<typeof setInterval>;

    if (busy) {
      showTimer = setTimeout(() => {
        shownAt.current = Date.now();
        setVisible(true);
        setPct(8);
        // Decelerating creep: big steps early, asymptotic near 90%, so the
        // bar always looks like it is making progress without ever
        // promising completion it can't deliver.
        creep = setInterval(() => {
          setPct((p) => (p >= 90 ? p : p + Math.max(0.4, (90 - p) * 0.06)));
        }, 120);
      }, SHOW_DELAY_MS);
    } else if (visible) {
      const elapsed = Date.now() - shownAt.current;
      const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
      hideTimer = setTimeout(() => {
        setPct(100);
        // Let the 100% width transition play out before unmounting.
        setTimeout(() => {
          setVisible(false);
          setPct(0);
        }, 220);
      }, wait);
    }

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearInterval(creep);
    };
  }, [busy, visible]);

  if (!visible) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-0.5"
      role="progressbar"
      aria-label="Loading"
      aria-busy="true"
    >
      <div
        className="h-full bg-teal transition-[width,opacity] duration-200 ease-out"
        style={{ width: `${pct}%`, opacity: pct >= 100 ? 0 : 1 }}
      />
    </div>
  );
}

/**
 * Suspense fallback for lazy routes.
 *
 * Renders the supplied skeleton (so the page keeps its shape instead of
 * collapsing to a spinner) and holds the top progress bar open for as long
 * as the chunk is in flight.
 */
export function RouteFallback({ children }: { children?: React.ReactNode }) {
  useEffect(() => startRouteLoad(), []);
  return <>{children ?? null}</>;
}
